// ════════════════════════════════════════════════════════════
// InformeAcciones — firmas táctiles y envío por email del
// informe de intervención. Guardar en: src/components/InformeAcciones.tsx
// ════════════════════════════════════════════════════════════
import React, { useRef, useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Chip
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import EmailIcon from '@mui/icons-material/Email';
import {
  guardarFirmaIntervencion, enviarInformeEmail, getCliente
} from '../api';

interface Props {
  intervencion: any;
  onActualizado?: () => void;
}

export default function InformeAcciones({ intervencion, onActualizado }: Props) {
  const [firmaDialog, setFirmaDialog] = useState<null | 'tecnico' | 'cliente'>(null);
  const [emailDialog, setEmailDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [guardandoFirma, setGuardandoFirma] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const hayTrazo = useRef(false);

  // ── Canvas de firma ──
  useEffect(() => {
    if (!firmaDialog) return;
    // Pequeña espera para que el canvas exista en el DOM del diálogo
    const t = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.strokeStyle = '#1a1a2e';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
      hayTrazo.current = false;
    }, 100);
    return () => clearTimeout(t);
  }, [firmaDialog]);

  const posicion = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const empezarTrazo = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = posicion(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    dibujando.current = true;
  };

  const moverTrazo = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dibujando.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = posicion(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hayTrazo.current = true;
  };

  const acabarTrazo = () => { dibujando.current = false; };

  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, rect.width, rect.height);
    hayTrazo.current = false;
  };

  const guardarFirma = async () => {
    if (!firmaDialog || !canvasRef.current) return;
    if (!hayTrazo.current) { alert('El recuadro de firma está vacío'); return; }
    setGuardandoFirma(true);
    try {
      const imagen = canvasRef.current.toDataURL('image/png');
      await guardarFirmaIntervencion(intervencion.id, firmaDialog, imagen);
      setFirmaDialog(null);
      onActualizado?.();
      alert('✅ Firma guardada. Se incluirá en el PDF del informe.');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la firma');
    } finally {
      setGuardandoFirma(false);
    }
  };

  // ── Email ──
  const abrirEmail = async () => {
    let precargado = '';
    try {
      if (intervencion.cliente_id) {
        const cliente = await getCliente(intervencion.cliente_id);
        precargado = cliente.contacto_email || cliente.email || '';
      }
    } catch { /* sin precarga */ }
    setEmail(precargado);
    setEmailDialog(true);
  };

  const enviar = async () => {
    setEnviando(true);
    try {
      const res = await enviarInformeEmail(intervencion.id, email.trim());
      setEmailDialog(false);
      alert(`✅ ${res.mensaje || 'Informe enviado'}`);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al enviar el informe');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Button variant="outlined" startIcon={<DrawIcon />} onClick={() => setFirmaDialog('tecnico')}>
        Firma técnico {intervencion.firma_tecnico ? '✓' : ''}
      </Button>
      <Button variant="outlined" startIcon={<DrawIcon />} onClick={() => setFirmaDialog('cliente')}>
        Firma cliente {intervencion.firma_cliente ? '✓' : ''}
      </Button>
      <Button variant="outlined" startIcon={<EmailIcon />} onClick={abrirEmail}>
        Enviar por email
      </Button>

      {/* DIALOG FIRMA */}
      <Dialog open={!!firmaDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setFirmaDialog(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>
          ✍️ Firma del {firmaDialog === 'tecnico' ? 'técnico' : 'cliente'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Firma dentro del recuadro con el dedo, el lápiz o el ratón.
          </Typography>
          <Box sx={{ border: '2px dashed #F5C200', borderRadius: 2, overflow: 'hidden' }}>
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: 200, display: 'block', touchAction: 'none', cursor: 'crosshair' }}
              onPointerDown={empezarTrazo}
              onPointerMove={moverTrazo}
              onPointerUp={acabarTrazo}
              onPointerLeave={acabarTrazo}
            />
          </Box>
          {((firmaDialog === 'tecnico' && intervencion.firma_tecnico) ||
            (firmaDialog === 'cliente' && intervencion.firma_cliente)) && (
            <Chip label="Ya existe una firma guardada: al guardar la sustituirás"
              color="warning" size="small" sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={limpiarFirma}>Limpiar</Button>
          <Button onClick={() => setFirmaDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarFirma} disabled={guardandoFirma}
            sx={{ bgcolor: '#4f46e5' }}>
            Guardar firma
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG EMAIL */}
      <Dialog open={emailDialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setEmailDialog(false); } }} maxWidth="sm" fullWidth>
        <DialogTitle>📧 Enviar informe al cliente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Se enviará el PDF del informe INT-{intervencion.id} como adjunto.
            {(!intervencion.firma_tecnico || !intervencion.firma_cliente) &&
              ' Aviso: el informe aún no tiene todas las firmas.'}
          </Typography>
          <TextField
            fullWidth label="Email de destino" type="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={enviar}
            disabled={enviando || !email.includes('@')} sx={{ bgcolor: '#4f46e5' }}>
            {enviando ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}